import { useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import RoleCard from "../../components/roles/role_card";
import { useAuthRedirect } from "../../hooks/useAuthRedirect";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function RolesPreview() {
  const query = useQuery();
  const navigate = useNavigate();

  const [candidateStream, setCandidateStream] = useState("");
  const [selectedStream, setSelectedStream] = useState("all");
  const [streamsArr, setStreamsArr] = useState([]);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const [roles, setRoles] = useState([]);

  const streamOptions = [
    { value: "all", label: "All Streams" },
    { value: "swe", label: "Software Engineering" },
    { value: "generalist", label: "Generalist" },
    { value: "marketing", label: "Marketing" },
    { value: "sales", label: "Sales" }
  ];

  // 1) Ensure user is logged in
  const { isAuthenticated, isChecking } = useAuthRedirect();

  // 2) Fetch the candidate's stream
  useEffect(() => {
    if (!isAuthenticated || isChecking) return;
    
    const fetchCandidateStream = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          return;
        }

        const response = await axios.get("/api/candidates/fetchCandidate", {
          headers: { Authorization: `Bearer ${token}` },
        });

        const candidateFields = response.data.candidate.fields;
        let streamValue =
          candidateFields["Do not edit - new Stream (for visibility column)"] ||
          "";

        if (Array.isArray(streamValue)) {
          if (streamValue.length > 0) {
            streamValue = streamValue[0];
          } else {
            streamValue = "";
          }
        }

        setCandidateStream(streamValue);
      } catch (error) {
        console.error("Error fetching candidate stream:", error);
      }
    };

    fetchCandidateStream();
  }, [isAuthenticated, isChecking]);

  // 3) Parse candidateStream into an array
  useEffect(() => {
    if (!candidateStream) {
      setStreamsArr([]);
      return;
    }
    const arr = candidateStream.split(",").map((s) => s.trim().toLowerCase());
    setStreamsArr(arr);
  }, [candidateStream]);

  // Auto-increment progress
  useEffect(() => {
    let interval;
    if (loading) {
      interval = setInterval(() => {
        setProgress((prev) => {
          if (prev < 90) return prev + 5;
          return prev;
        });
      }, 200);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [loading]);

  // 4) Fetch roles including preview ones
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setLoading(true);
        setProgress(0);

        const token = localStorage.getItem("token");
        let streamToFetch = selectedStream;
        
        if (selectedStream === "all") {
          streamToFetch = "Software Engineering,Generalist,Marketing,Sales";
        }
        else if (selectedStream === "generalist" || selectedStream === "marketing") {
          streamToFetch = "Generalist,Marketing";
        }
        else if (selectedStream === "swe") {
          streamToFetch = "Software Engineering";
        }
        else if (selectedStream === "sales") {
          streamToFetch = "Sales";
        }

        const response = await axios.get("/api/roles/fetchPreviewRoles", {
          headers: { Authorization: `Bearer ${token}` },
          params: { streams: streamToFetch },
        });

        const allRoles = response.data.roles || [];
        const roleOptions = allRoles.map((role) => ({
          id: role.id,
          "Role Title": role["Primary Field"] || role["Role Title"],
          "URL ID": role["URL ID"],
          "Role Drop Status": role["Role Drop Status (list visibility)"] || "",
          "Stream": role["Stream"] || [],
          Compensation: role["Compensation"],
          "Funding Stage (from Startup)": role["Funding Stage (from Startup)"] ?? "",
          "Team Size (from Startup)": role["Team Size (from Startup)"] ?? "",
          "Location (from Startup)": role["Location (from Startup)"] ?? "",
          "Spotlight Context": role["Spotlight Context"] ?? "",
          "URL Dropbox cleaned (from Spotlight)": role["URL Dropbox cleaned (from Spotlight)"]?.[0] ?? null,
          "URL Loom cleaned (from Spotlight)": role["URL Loom cleaned (from Spotlight)"]?.[0] ?? null,
          "Role Cover Image (from Startup)": role["Role Cover Image (from Startup)"]?.[0]?.url ||
            "https://placehold.co/400x300/e2e8f0/64748b?text=No+Image",
          spotlightDetails: role.spotlightDetails || [],
          "Name (from Startup)": role["Name (from Startup)"] || "",
          "Startup Description (from Startup)": role["Startup Description (from Startup)"] || "",
          "Website (from Startup)": role["Website (from Startup)"] || "",
          "Specific Experience": role["Specific Experience"] || "",
          "Interview Process": role["Interview Process"] || "",
          "Job Description PDF": role["Job Description PDF"] || "",
          "Working Style": role["Working Style"] ?? "",
        }));

        setRoles(roleOptions);
      } catch (err) {
        console.error("Error fetching roles:", err);
      } finally {
        setProgress(100);
        setTimeout(() => {
          setLoading(false);
        }, 500);
      }
    };

    fetchRoles();
  }, [selectedStream]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleStreamChange = (e) => {
    setSelectedStream(e.target.value);
  };

  const filteredRoles = roles.filter((role) =>
    role["Role Title"]?.toLowerCase()?.includes(searchQuery.toLowerCase())
  );

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-4">Preview Testing Page - All Roles</h1>
        <p className="text-gray-600 mb-4">
          This page shows all roles with status: "Will go live", "Live", or "Preview"
        </p>
        
        {/* Filters Section */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          {/* Search Bar */}
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search roles..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="w-full p-2 border rounded-lg"
            />
          </div>
          
          {/* Stream Filter */}
          <div className="md:w-64">
            <select
              value={selectedStream}
              onChange={handleStreamChange}
              className="w-full p-2 border rounded-lg bg-white"
            >
              {streamOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Loading Progress */}
        {loading && (
          <div className="w-full h-2 bg-gray-200 rounded-full mb-6">
            <div
              className="h-full bg-blue-500 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}

        {/* Role Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredRoles.map((role) => (
            <div key={role.id} className="relative">
              <div className="absolute top-0 right-0 z-10 bg-blue-500 text-white px-2 py-1 rounded-tr-lg rounded-bl-lg text-sm">
                {role["Role Drop Status"]}
              </div>
              <div className="absolute top-0 left-0 z-10 bg-green-500 text-white px-2 py-1 rounded-tl-lg rounded-br-lg text-sm">
                {Array.isArray(role.Stream) ? role.Stream.join(", ") : role.Stream}
              </div>
              <RoleCard
                role={role}
                currentlyPlaying={currentlyPlaying}
                setCurrentlyPlaying={setCurrentlyPlaying}
              />
            </div>
          ))}
        </div>

        {!loading && filteredRoles.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500">No roles found</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default RolesPreview; 